/**
 * Triveny.ai TOTP enrollment.
 *
 * Two-step flow:
 *   1. Fetch /api/platform/auth/2fa/setup-totp → display QR + secret;
 *      ask the user to scan it with Google Authenticator / Authy / 1Password
 *      and enter the first 6-digit code.
 *   2. Submit the code to /api/platform/auth/2fa/verify-totp-setup. On
 *      success, surface the 10 backup codes ONCE so the user can save
 *      them, then redirect to /login (the next sign-in will require a
 *      TOTP code).
 *
 * Reached automatically after the first successful Triveny login when
 * the user's mfa_enabled flag is false (see app.tsx redirect). Also
 * accessible directly from a settings page once that ships.
 *
 * WeaveIn users do not see this page — the route is registered globally
 * but a config.isTriveny guard returns to /login if a non-Triveny user
 * lands here (their flow is /settings/2fa with the legacy UI).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ShieldCheck, KeyRound, Loader2, AlertCircle } from 'lucide-react';
import { config } from '@/src/config';
import {
  TvButton,
  TvCard,
  TvEyebrow,
  TvLabel,
  TvPageBar,
  TvPill,
  tvTokens,
} from '@/src/platform/shared/components/triveny';

interface SetupResponse {
  secret: string;
  qr_code: string;
  issuer: string;
  message: string;
}

interface VerifyResponse {
  success: boolean;
  message: string;
  backup_codes: string[];
}

export default function Setup2FAPage() {
  const navigate = useNavigate();

  // WeaveIn users don't use this page; redirect them to the legacy settings.
  useEffect(() => {
    if (!config.isTriveny) navigate('/login', { replace: true });
  }, [navigate]);

  const [setupData, setSetupData] = useState<SetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** If the user lands here but 2FA is already enrolled, surface a recovery
   *  CTA instead of leaving them stuck on a 409. */
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);

  // Mount: first ask /methods whether the user is already enrolled. If yes,
  // skip the QR + setup-totp dance and route them out. Only call /setup-totp
  // when we're certain mfa_enabled is currently false.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          navigate('/login', { replace: true });
          return;
        }

        // Pre-check current 2FA state. Cheap GET, idempotent.
        const methodsRes = await fetch(
          `${config.weaveinApi.baseUrl}/api/platform/auth/2fa/methods`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (methodsRes.ok) {
          const methods = await methodsRes.json();
          if (methods?.mfa_enabled) {
            if (!cancelled) {
              setAlreadyEnrolled(true);
              setLoading(false);
            }
            return;
          }
        }
        // Not enrolled yet — initiate TOTP setup.
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/platform/auth/2fa/setup-totp`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.status === 409) {
          // Race: methods said not-enrolled but setup says otherwise. Treat
          // as already enrolled and recover.
          if (!cancelled) setAlreadyEnrolled(true);
          return;
        }
        if (!res.ok) throw new Error(data.detail || 'Failed to initialize TOTP setup');
        if (!cancelled) setSetupData(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Failed to initialize TOTP setup');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const continueToApp = () => {
    // The user is already enrolled — clear any stale auth_token (which has
    // the pre-enrollment mfa_enabled=false claim) and bounce them to /login
    // so they re-auth through the proper 2FA challenge flow.
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('device_token');
    navigate('/login', { replace: true });
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the full 6-digit code from your authenticator app');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(
        `${config.weaveinApi.baseUrl}/api/platform/auth/2fa/verify-totp-setup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
        },
      );
      const data: VerifyResponse = await res.json();
      if (!res.ok) throw new Error((data as any).detail || 'Invalid code');
      setBackupCodes(data.backup_codes);
    } catch (e: any) {
      setError(e.message || 'Verification failed. Double-check your code and try again.');
      setCode('');
    } finally {
      setVerifying(false);
    }
  };

  const finish = () => {
    // After enrollment is complete the user must re-authenticate so the
    // session now reflects mfa_enabled=true. Clear tokens and bounce.
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('device_token');
    navigate('/login', { replace: true });
  };

  const copySecret = () => {
    if (setupData?.secret) navigator.clipboard.writeText(setupData.secret);
  };

  const copyBackup = () => {
    if (backupCodes) navigator.clipboard.writeText(backupCodes.join('\n'));
  };

  return (
    <div className="triveny-platform" style={{ minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans }}>
      <TvPageBar
        eyebrow="Account security"
        title={
          alreadyEnrolled
            ? 'Authenticator already enabled'
            : backupCodes
            ? 'Save your recovery codes'
            : 'Set up authenticator app'
        }
      />

      <div style={{ maxWidth: 540, margin: '32px auto', padding: '0 22px' }}>
        {/* Error banner */}
        {error && !alreadyEnrolled && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              marginBottom: 16,
              borderRadius: tvTokens.r,
              background: tvTokens.dangerSoft,
              color: tvTokens.dangerInk,
              fontSize: 13,
            }}
          >
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Already-enrolled recovery card. The user lands here if they
            navigated back to /setup-2fa after enrollment, or refreshed
            the backup-codes screen, or got bounced here by stale state. */}
        {alreadyEnrolled && (
          <TvCard
            header={<TvLabel size="lg">You're already enrolled in 2FA</TvLabel>}
            headerActions={<TvPill variant="success">2FA ACTIVE</TvPill>}
          >
            <div style={{ padding: '18px 16px' }}>
              <p style={{ fontSize: 13, color: tvTokens.ink500, marginTop: 0, marginBottom: 14 }}>
                Two-factor authentication is already turned on for this account.
                Sign in normally — you'll be prompted for the code from your
                authenticator app after entering your password.
              </p>
              <p style={{ fontSize: 12, color: tvTokens.ink500, marginTop: 0, marginBottom: 16 }}>
                Lost your authenticator or backup codes? Ask another platform
                administrator to disable 2FA on your account, then re-enroll.
              </p>
              <TvButton variant="primary" icon={KeyRound} onClick={continueToApp}>
                Continue to sign in
              </TvButton>
            </div>
          </TvCard>
        )}

        {/* Loading */}
        {loading && !setupData && !alreadyEnrolled && (
          <TvCard bodyPadding={28}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: tvTokens.ink500 }}>
              <Loader2 size={16} className="animate-spin" />
              <span>Preparing your authenticator setup…</span>
            </div>
          </TvCard>
        )}

        {/* Step 1: scan + verify */}
        {!loading && setupData && !backupCodes && !alreadyEnrolled && (
          <TvCard
            header={<TvLabel size="lg">Step 1 of 2 — Scan QR code</TvLabel>}
            headerActions={<TvPill variant="attention">REQUIRED</TvPill>}
          >
            <div style={{ padding: '18px 16px' }}>
              <p style={{ fontSize: 13, color: tvTokens.ink500, marginTop: 0, marginBottom: 16 }}>
                Open Google Authenticator, Microsoft Authenticator, 1Password, or Authy
                and scan this QR code:
              </p>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 12,
                  background: '#fff',
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: tvTokens.r,
                  marginBottom: 14,
                }}
              >
                <img src={setupData.qr_code} alt="TOTP enrollment QR code" style={{ width: 196, height: 196 }} />
              </div>

              <TvEyebrow style={{ marginBottom: 6 }}>Or enter this code manually</TvEyebrow>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: tvTokens.r,
                  background: tvTokens.paper2,
                  marginBottom: 18,
                }}
              >
                <code style={{ flex: 1, fontFamily: tvTokens.fontMono, fontSize: 13, color: tvTokens.ink, wordBreak: 'break-all' }}>
                  {setupData.secret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  aria-label="Copy secret"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: tvTokens.ink500, padding: 4 }}
                >
                  <Copy size={14} />
                </button>
              </div>

              <form onSubmit={verify}>
                <TvEyebrow style={{ marginBottom: 6 }}>Enter the 6-digit code shown in your app</TvEyebrow>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontFamily: tvTokens.fontMono,
                    fontSize: 18,
                    letterSpacing: '0.4em',
                    textAlign: 'center',
                    border: `1px solid ${tvTokens.border}`,
                    borderRadius: tvTokens.r,
                    background: '#fff',
                    color: tvTokens.ink,
                    marginBottom: 14,
                  }}
                />
                <TvButton
                  variant="primary"
                  type="submit"
                  disabled={verifying || code.length !== 6}
                  icon={ShieldCheck}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {verifying ? 'Verifying…' : 'Verify and enable'}
                </TvButton>
              </form>
            </div>
          </TvCard>
        )}

        {/* Step 2: backup codes */}
        {backupCodes && !alreadyEnrolled && (
          <TvCard
            header={<TvLabel size="lg">Step 2 of 2 — Save recovery codes</TvLabel>}
            headerActions={<TvPill variant="success">2FA ENABLED</TvPill>}
            accent
          >
            <div style={{ padding: '18px 16px' }}>
              <p style={{ fontSize: 13, color: tvTokens.ink500, marginTop: 0, marginBottom: 14 }}>
                Save these recovery codes in a password manager. Each can be used <strong>once</strong> if
                you lose access to your authenticator app. <strong>This is the only time they will be shown.</strong>
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  padding: 14,
                  marginBottom: 14,
                  background: tvTokens.paper2,
                  border: `1px solid ${tvTokens.border}`,
                  borderRadius: tvTokens.r,
                  fontFamily: tvTokens.fontMono,
                  fontSize: 12,
                  color: tvTokens.ink,
                }}
              >
                {backupCodes.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <TvButton variant="ghost" icon={Copy} onClick={copyBackup}>
                  Copy all
                </TvButton>
                <span style={{ flex: 1 }} />
                <TvButton variant="primary" icon={KeyRound} onClick={finish}>
                  I've saved them — sign in
                </TvButton>
              </div>
            </div>
          </TvCard>
        )}
      </div>
    </div>
  );
}
